import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Activity, BadgeCheck, Ban, Check, CheckCircle2, Copy, FileSignature,
    Link2, Loader2, Mail, RefreshCcw, Search, Shield, Trash2, User, UserX,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePartnerAuth } from "@/context/PartnerAuthContext";
import { StudioFormSettings, InsidersList, HomeSectionTitles, StudioInbox } from "@/components/partner/StudioFormSettings";
import PartnerQuestionsInbox from "@/components/partner/PartnerQuestionsInbox";

/**
 * PartnerOwnerAdmin — Owner-only partner management view.
 *
 * Niche-agnostic. Reads from /api/owner/* which is server-side gated by
 * the partner JWT's email matching OWNER_EMAILS env + OWNER_SEED floor.
 * UI hiding alone is NOT the security boundary — the backend rejects
 * non-owner partner tokens with 403.
 */
export default function PartnerOwnerAdmin() {
    const { partner, status } = usePartnerAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("partners"); // partners | audit
    const [partners, setPartners] = useState([]);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [working, setWorking] = useState(null); // email+action pair

    // Hard route guard — bounce non-owners back to dashboard.
    useEffect(() => {
        if (status === "guest") navigate("/partner/auth", { replace: true });
        else if (status === "authed" && partner && !partner.is_owner) {
            navigate("/partner/dashboard", { replace: true });
        }
    }, [status, partner, navigate]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, a] = await Promise.all([
                api.get("/owner/partners"),
                api.get("/owner/audit", { params: { limit: 50 } }),
            ]);
            setPartners(p.data.partners || []);
            setAudit(a.data.audit || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load admin data.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (partner?.is_owner) load();
    }, [partner, load]);

    const filtered = useMemo(() => {
        if (!query.trim()) return partners;
        const q = query.toLowerCase();
        return partners.filter((p) =>
            [p.email, p.legal_name, p.display_name]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q)),
        );
    }, [partners, query]);

    const act = async (email, verb, body = {}) => {
        setWorking(`${email}:${verb}`);
        try {
            if (verb === "remove") {
                if (!window.confirm(`Permanently remove ${email}? This deletes their agreement and affiliate rows too.`)) {
                    setWorking(null); return;
                }
                await api.delete(`/owner/partners/${encodeURIComponent(email)}`);
            } else if (verb === "resend-invite") {
                const { data } = await api.post(`/owner/partners/${encodeURIComponent(email)}/resend-invite`, { expires_days: 14 });
                await navigator.clipboard?.writeText(data.invite_url).catch(() => {});
                toast.success("Fresh invite link copied to clipboard.");
                load();
                setWorking(null); return;
            } else {
                await api.post(`/owner/partners/${encodeURIComponent(email)}/${verb}`, body);
            }
            toast.success(`Done · ${verb}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || `Could not ${verb} ${email}`);
        } finally {
            setWorking(null);
        }
    };

    if (status !== "authed" || !partner?.is_owner) {
        return (
            <div className="min-h-[70vh] grid place-items-center bg-forest text-cream">
                <Loader2 className="animate-spin" size={28} />
            </div>
        );
    }

    return (
        <div className="min-h-[90vh] bg-forest text-cream" data-testid="partner-owner-admin">
            <section className="max-w-7xl mx-auto px-5 md:px-10 py-14">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <span className="divider-rule text-gold inline-flex items-center gap-1.5">
                            <Shield size={12} /> Owner Admin
                        </span>
                        <h1 className="font-serif text-4xl md:text-5xl text-cream mt-4 leading-[1.05]">
                            Partner management
                        </h1>
                        <p className="mt-3 text-cream/60 max-w-2xl text-sm leading-relaxed">
                            Signed in as <strong className="text-cream">{partner.email}</strong>. Server-side owner enforcement is live — this page is unreachable without an owner token.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to="/partner/dashboard" className="btn-pill btn-ghost text-[0.65rem]" data-testid="owner-admin-back">
                            Back to dashboard
                        </Link>
                        <button onClick={load} className="btn-pill btn-ghost text-[0.65rem]" data-testid="owner-admin-refresh">
                            <RefreshCcw size={12} /> Refresh
                        </button>
                    </div>
                </div>

                <div className="mt-10 flex gap-2 flex-wrap">
                    {[
                        { k: "partners", label: `Partners (${partners.length})`, icon: User },
                        { k: "audit", label: `Activity log (${audit.length})`, icon: Activity },
                        { k: "inbox", label: "Studio inbox", icon: Mail },
                        { k: "pq", label: "Partner questions", icon: Mail },
                        { k: "studio", label: "Studio form", icon: Mail },
                        { k: "insiders", label: "Insiders", icon: User },
                        { k: "home", label: "Homepage titles", icon: Mail },
                    ].map((t) => {
                        const Icon = t.icon;
                        return (
                            <button
                                key={t.k}
                                onClick={() => setTab(t.k)}
                                aria-selected={tab === t.k}
                                data-testid={`owner-admin-tab-${t.k}`}
                                className={`px-4 py-2.5 text-[0.65rem] tracking-[0.28em] uppercase rounded-sm border transition-colors inline-flex items-center gap-1.5 ${
                                    tab === t.k
                                        ? "bg-cream text-forest border-cream"
                                        : "bg-forest text-cream/60 border-cream/15 hover:text-cream"
                                }`}
                            >
                                <Icon size={12} /> {t.label}
                            </button>
                        );
                    })}
                </div>

                {loading ? (
                    <div className="mt-10 flex items-center gap-2 text-cream/70 text-sm">
                        <Loader2 size={14} className="animate-spin" /> Loading…
                    </div>
                ) : tab === "partners" ? (
                    <div className="mt-6">
                        <div className="flex items-center gap-2 mb-5">
                            <Search size={14} className="text-cream/50" />
                            <input
                                type="text"
                                placeholder="Search name or email…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="owner-admin-search"
                                className="flex-1 bg-cream/5 border border-cream/10 rounded-sm text-cream px-4 py-2.5 text-sm placeholder:text-cream/40 focus:outline-none focus:border-gold max-w-md"
                            />
                        </div>
                        <div className="border border-cream/10 rounded-sm overflow-hidden" data-testid="owner-admin-partners-table">
                            {filtered.length === 0 ? (
                                <div className="p-6 text-cream/60 text-sm">
                                    {query ? "No partners match that search." : "No partners registered yet."}
                                </div>
                            ) : (
                                <div className="divide-y divide-cream/10">
                                    {filtered.map((p) => (
                                        <PartnerRow
                                            key={p.email}
                                            p={p}
                                            working={working}
                                            onAct={act}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : tab === "pq" ? (
                    <PartnerQuestionsInbox />
                ) : tab === "inbox" ? (
                    <StudioInbox />
                ) : tab === "studio" ? (
                    <StudioFormSettings />
                ) : tab === "insiders" ? (
                    <InsidersList />
                ) : tab === "home" ? (
                    <HomeSectionTitles />
                ) : (
                    <div className="mt-6 border border-cream/10 rounded-sm" data-testid="owner-admin-audit-table">
                        {audit.length === 0 ? (
                            <div className="p-6 text-cream/60 text-sm">No admin actions recorded yet.</div>
                        ) : (
                            <div className="divide-y divide-cream/10">
                                {audit.map((a, i) => (
                                    <div key={i} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                                        <div className="min-w-0">
                                            <div className="text-cream text-sm font-medium">
                                                <span className="text-gold">{a.action}</span>
                                                {a.target_email && (
                                                    <> → <span className="text-cream">{a.target_email}</span></>
                                                )}
                                            </div>
                                            <div className="text-cream/50 text-[0.65rem] mt-1">
                                                by {a.actor_email} · {a.at ? new Date(a.at).toLocaleString() : "—"}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
}

function PartnerRow({ p, working, onAct }) {
    const key = (verb) => working === `${p.email}:${verb}`;
    const [open, setOpen] = useState(false);
    const checklist = [
        { label: "Identity verified", ok: p.identity_verified, testid: "chk-identity" },
        { label: "Agreement signed", ok: p.agreement_signed, testid: "chk-agreement" },
        { label: "Payment on file", ok: p.paid_access, testid: "chk-payment" },
    ];
    const readyForPayouts = p.identity_verified && p.agreement_signed;

    return (
        <div className="px-4 py-4" data-testid={`owner-partner-row-${p.email}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="text-cream font-medium flex items-center gap-2 flex-wrap">
                        <span className="truncate">{p.display_name || p.legal_name || p.email.split("@")[0]}</span>
                        {p.is_owner && (
                            <span className="text-[0.6rem] tracking-[0.25em] uppercase bg-gold/25 text-gold px-2 py-0.5 rounded-full">
                                Owner
                            </span>
                        )}
                        <span className={`text-[0.6rem] tracking-[0.25em] uppercase px-2 py-0.5 rounded-full ${
                            p.status === "suspended"
                                ? "bg-[#b14c3c]/25 text-[#f4a494]"
                                : "bg-olive/25 text-cream"
                        }`}>
                            {p.status}
                        </span>
                        {readyForPayouts && (
                            <span className="text-[0.6rem] tracking-[0.25em] uppercase bg-olive/30 text-cream px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                <BadgeCheck size={10} /> Payout-ready
                            </span>
                        )}
                    </div>
                    <div className="text-cream/55 text-xs mt-1 truncate">{p.email}</div>
                    <div className="text-cream/40 text-[0.65rem] mt-1">
                        Signed up {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                        {p.legal_name && <> · Legal name on file</>}
                        {p.affiliate_slug && <> · Affiliate /{p.affiliate_slug}</>}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                        onClick={() => setOpen((o) => !o)}
                        className="btn-pill btn-ghost text-[0.6rem]"
                        data-testid={`owner-partner-expand-${p.email}`}
                    >
                        {open ? "Hide" : "Details"}
                    </button>
                    {p.status === "suspended" ? (
                        <button
                            onClick={() => onAct(p.email, "unsuspend")}
                            disabled={!!working || p.is_owner}
                            className="btn-pill btn-ghost text-[0.6rem]"
                            data-testid={`owner-partner-unsuspend-${p.email}`}
                        >
                            {key("unsuspend") ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                            Unsuspend
                        </button>
                    ) : (
                        <button
                            onClick={() => onAct(p.email, "suspend", { note: "" })}
                            disabled={!!working || p.is_owner}
                            title={p.is_owner ? "Owner cannot be suspended" : ""}
                            className="btn-pill btn-ghost text-[0.6rem] disabled:opacity-30"
                            data-testid={`owner-partner-suspend-${p.email}`}
                        >
                            {key("suspend") ? <Loader2 size={10} className="animate-spin" /> : <Ban size={10} />}
                            Suspend
                        </button>
                    )}
                    <button
                        onClick={() => onAct(p.email, "verify-identity")}
                        disabled={!!working || p.identity_verified}
                        className="btn-pill btn-ghost text-[0.6rem] disabled:opacity-40"
                        data-testid={`owner-partner-verify-${p.email}`}
                    >
                        {key("verify-identity") ? <Loader2 size={10} className="animate-spin" /> : <BadgeCheck size={10} />}
                        {p.identity_verified ? "ID verified" : "Verify ID"}
                    </button>
                    <button
                        onClick={() => onAct(p.email, "resend-invite")}
                        disabled={!!working}
                        className="btn-pill btn-ghost text-[0.6rem]"
                        data-testid={`owner-partner-resend-${p.email}`}
                    >
                        {key("resend-invite") ? <Loader2 size={10} className="animate-spin" /> : <Mail size={10} />}
                        Invite link
                    </button>
                    <button
                        onClick={() => onAct(p.email, "remove")}
                        disabled={!!working || p.is_owner}
                        title={p.is_owner ? "Owner cannot be removed" : "Remove partner"}
                        className="btn-pill btn-ghost text-[0.6rem] disabled:opacity-30"
                        data-testid={`owner-partner-remove-${p.email}`}
                    >
                        {key("remove") ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
                        Remove
                    </button>
                </div>
            </div>
            {open && (
                <div className="mt-3 bg-cream/5 border border-cream/10 rounded-sm p-4 text-xs text-cream/75 space-y-2">
                    <div className="grid sm:grid-cols-3 gap-2">
                        {checklist.map((c) => (
                            <div
                                key={c.label}
                                data-testid={`owner-partner-${p.email}-${c.testid}`}
                                className={`px-3 py-2 rounded-sm border flex items-center gap-2 ${
                                    c.ok ? "border-olive/40 bg-olive/10 text-cream" : "border-cream/15 bg-forest/40 text-cream/55"
                                }`}
                            >
                                {c.ok ? <CheckCircle2 size={12} className="text-olive" /> : <span className="w-3 h-3 rounded-full border border-cream/30 inline-block" />}
                                <span>{c.label}</span>
                            </div>
                        ))}
                    </div>
                    <div className="pt-1 text-cream/60 leading-relaxed">
                        {p.agreement_version && <>Agreement version: <code className="text-cream">{p.agreement_version}</code>. </>}
                        {p.addendum_a_signed && <>Addendum A signed. </>}
                        {!readyForPayouts && (
                            <span className="text-[#f4a494]">
                                Payouts, affiliate publishing, and advances remain locked until identity and agreement are complete.
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
